import { TrashIcon } from "@heroicons/react/outline";
import { Checkbox } from "../../common/components/Input";
import type { Dispatch, Key, SetStateAction } from "react";
import { useState, useEffect } from "react";

import { useTranslation } from "react-i18next";
import ModalConfirmation from "../../common/components/Modal/ModalConfirmation";
import TableComp from "../../common/components/Table/Table";
import { useAppDispatch, useAppSelector } from "../../common/state/hooks";
import {
  closeConfirmationModal,
  openConfirmationModal,
} from "../../common/state/slice/modal/modalSlice";
import {
  PROJECT_GROUP_LIST_COMPONENT_ID,
  PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
} from "../../features/projectGroups/components/ProjectGroupList";
import {
  deleteMultipleProjectGroup,
  fetchAllCurrentElements,
  fetchProjectGroupsWithProjects,
  getCurrentSearchCount,
  getSearchState,
  removeElementsOfProject,
  setSelectedProjectGroupFromId,
  setSearchPage,
} from "../../features/projectGroups/state/projectGroupsSlice";
import type { IProjectGroupWithProjects } from "../../features/projectGroups/state/projectGroupsSlice";
import PGBadge from "../../common/components/PGBadge/PGBadge";
import {
  deleteMultipleProject,
  setCurrentProjectFromId,
} from "../../features/projects/state/projectsSlice";
import Badge from "../../common/components/Badge/Badge";
import { IsLoading, useLoading } from "../../common/components/AppState/IsLoading";
import { LoadingState } from "../../common/state/slice/appStateSlice";
import DisplayActions from "../../common/util/DisplayActions";
import NoItemsList from "../../common/util/NoItemsList";
import { prettifyFileSizeInBytes } from "../../common/util/prettifyFileSizeInBytes";
import { dateConfig } from "src/common/util/DateConfig";
import { getCurrentOrganization } from "src/features/profile/state/profileSlice";

import type { IMedia } from "src/model/unityObject";
import type { IProject, IProjectGroupElementProject } from "src/model/model";
import CheckAll from "src/common/components/UsersTable/CheckAll";
import MadeWithAiIcon from "src/common/components/MadeWithAiIcon/MadeWithAiIcon";
import hasMediaBeenGeneratedByAi from "src/common/util/HasMediaBeenGeneratedByAi";
import { PaginationControls } from "src/common/components/PaginationControls/PaginationControls";
import { useGalleryPagination } from "src/features/gallery/useGalleryPagination";

export type ProjectGroupListItem =
  | IMedia
  | IProject
  | IProjectGroupWithProjects
  | IProjectGroupElementProject;

const isMediaItem = (item: ProjectGroupListItem): item is IMedia => "filename" in item;
const isProjectGroupItem = (item: ProjectGroupListItem): item is IProjectGroupWithProjects =>
  "auth_type" in item;
const isProjectItem = (
  item: ProjectGroupListItem,
): item is IProject | IProjectGroupElementProject => "need_login" in item;

export interface ItemsList {
  arrayItemsForList: ProjectGroupListItem[];
  setClickedProjectGroupId: (id: number) => void;
  handleClick(this: void, e: unknown): void;
  clickedProjectGroupId: number;
  isProjectList?: boolean;
  translateTypeAuth?: (type: string) => void;
  isCheck: Array<string>;
  isMediaList?: boolean;
  currentLocale?: "en" | "fr";
  isModalEditMedia?: boolean;
  openMediaDetail?(this: void, id: number, item: unknown, index: number): void;
  setIsCheck: Dispatch<SetStateAction<string[]>>;
  isCheckMedias?: Array<ProjectGroupListItem>;
  openModalDeleteMultipleMedias?: () => void;
  setIsCheckMedias?: Dispatch<SetStateAction<ProjectGroupListItem[]>>;
  onDeleteMedia?(this: void, media: IMedia): void;
  isModalEdit?: boolean;
  isProjectGroup?: boolean;
  setEditedProjectGroupId?(this: void, id: number, projectGroup: IProjectGroupWithProjects): void;
  hidePaginationControls?: boolean;
  isFilteredByPublishedProjects?: boolean;
}

const ModeListProjectGroup = ({
  arrayItemsForList,
  setClickedProjectGroupId,
  handleClick,
  clickedProjectGroupId,
  isProjectList = false,
  isMediaList = false,
  isProjectGroup = false,
  currentLocale,
  isCheck,
  setIsCheck,
  setIsCheckMedias = () => [],
  openModalDeleteMultipleMedias = () => [],
  openMediaDetail = () => [],
  onDeleteMedia = () => [],
  setEditedProjectGroupId,
  isModalEdit,
  hidePaginationControls,
  isFilteredByPublishedProjects = false,
}: ItemsList) => {
  const [isCheckAll, setIsCheckAll] = useState(false);
  const [stuffToDisplay, setStuffToDisplay] = useState<ProjectGroupListItem[]>([]);
  useEffect(() => {
    const incoming = Array.isArray(arrayItemsForList) ? arrayItemsForList : [];
    const clean = incoming.filter((item) => item != null);
    if (clean.length !== incoming.length) {
      console.error("ModeListProjectGroup: dropped null/undefined item(s) from list", incoming);
    }
    setStuffToDisplay(clean);
  }, [arrayItemsForList]);

  const filterPrivateProjectGroup = [...stuffToDisplay].filter((item) => {
    if ((isMediaItem(item) || isProjectItem(item)) && "organization" in item) {
      const organizationId = item.organization?.id;
      return organizationId !== undefined ? organizationId !== 3 : true;
    }
    return true;
  });
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const handlePageChange = useGalleryPagination({ mode: "projectGroup" });

  const currentOrg = useAppSelector(getCurrentOrganization);
  const projectGroupSearch = useAppSelector(getSearchState);
  const { page, limit = 20 } = projectGroupSearch;
  const totalProjectGroups = useAppSelector(getCurrentSearchCount) ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalProjectGroups / limit));
  const pgsLoadingState = useLoading(PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID);
  const projectGroupsAreLoading = pgsLoadingState < LoadingState.Success;

  useEffect(() => {
    setIsCheckAll(false);
    setIsCheck([]);
    setIsCheckMedias([]);
  }, [arrayItemsForList.length]);

  const canBeDeleted = (item: ProjectGroupListItem): boolean => {
    if (isMediaList) {
      return isMediaItem(item) ? item.organization?.id === Number(currentOrg?.id) : false;
    }

    if (isProjectList) {
      return true;
    }

    return isProjectGroupItem(item) ? !item.has_existing_build : false;
  };

  const deletableStuff = filterPrivateProjectGroup.filter((stuff) => {
    return canBeDeleted(stuff);
  });

  const checkAllProjectGroupComponent = () => {
    return (
      <CheckAll
        className={""}
        checked={isCheck.length === deletableStuff.length && isCheck.length > 0}
        onChange={handleSelectAll}
      />
    );
  };

  const handleClickGroupCheck = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    setIsCheck([...isCheck, String(id)]);

    if (!checked) {
      setIsCheck(isCheck.filter((item) => Number(item) !== Number(id)));
    }
  };

  const handleSelectAll = () => {
    if (isCheck.length > 0) {
      setIsCheck([]);
      setIsCheckMedias([]);
    } else {
      if (isMediaList) {
        setIsCheckMedias(filterPrivateProjectGroup);
        setIsCheck(filterPrivateProjectGroup.map((li) => String(li.id)));
      } else {
        setIsCheck(
          arrayItemsForList
            .filter((li) => canBeDeleted(li))
            .map((li) => {
              return String(li.id);
            }),
        );
      }
      setIsCheckAll(!isCheckAll);
    }
  };

  const openModalDeleteMultiple = () => {
    if (isMediaList) {
      openModalDeleteMultipleMedias();
    } else {
      dispatch(openConfirmationModal("deleteMultipleDeletesConfirmation"));
    }
  };

  const deleteSelectionButtonText = () => {
    if (isCheck.length === 1) {
      return t("general.deleteElement");
    } else {
      return t("general.deleteElements");
    }
  };

  const deleteSelection =
    isCheck.length === 1 ? t("general.deleteElementSingular") : t("general.deleteElementPlural");

  const RelevantBadge = (projectStatus: string) => {
    switch (projectStatus) {
      case "Edit":
      default:
        return (
          <Badge text={t("general.notPublished")} color="bg-red-500" textColor="text-red-100" />
        );
      case "Published":
        return (
          <Badge
            text={t("general.inPublication")}
            color="bg-green-500"
            textColor="text-green-100"
          />
        );
    }
  };

  const deleteProjectFromList = (projectId: number) => {
    setClickedProjectGroupId(projectId);
    dispatch(setCurrentProjectFromId(Number(projectId)));
    const project = arrayItemsForList.find((item) => Number(item.id) === Number(projectId));
    if (project && isProjectItem(project) && project.status === "Published") {
      dispatch(openConfirmationModal("deletePublishedProjectConfirmation"));
    } else {
      dispatch(openConfirmationModal("deleteProjectConfirmation"));
    }
  };
  const deleteProjectGroupFromList = (projectGroupItemId: number) => {
    setClickedProjectGroupId(projectGroupItemId);
    dispatch(setSelectedProjectGroupFromId(projectGroupItemId));
    void dispatch(
      fetchAllCurrentElements({
        projectGroupId: projectGroupItemId,
        componentId: PROJECT_GROUP_LIST_COMPONENT_ID,
      }),
    );
    dispatch(openConfirmationModal("deleteProjectGroupConfirmation"));
  };

  const translateTypeAuth = (type: string) => {
    switch (type) {
      case "classic":
        return t("pages.accessTypeTable.passwordLogin");
      case "simplified":
        return t("pages.accessTypeTable.simplifiedLogin");
      case "none":
      default:
        return t("pages.accessTypeTable.noLogin");
    }
  };

  const getColumnValues = stuffToDisplay.map((itemsForList, i: Key | null | undefined) => {
    const displayCheckbox = () => {
      if (isMediaList) {
        if (isMediaItem(itemsForList) && itemsForList.organization?.id !== 3 && isModalEdit) {
          return (
            <Checkbox
              key={`key-checkbox-list-${i}`}
              checked={isCheck.includes(String(itemsForList.id))}
              id={String(itemsForList.id)}
              value={JSON.stringify(itemsForList)}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                handleClick(e);
              }}
            />
          );
        }
      } else {
        if (canBeDeleted(itemsForList))
          return (
            <Checkbox
              key={`key-checkbox-list-${i}`}
              checked={isCheck?.includes(String(itemsForList.id))}
              id={String(itemsForList.id)}
              value={itemsForList.id}
              onClick={(e) => {
                e.stopPropagation();
              }}
              onChange={(e) => {
                handleClickGroupCheck(e);
              }}
            />
          );
      }
      return <></>;
    };
    const name = itemsForList.name;

    const isDeleteDisabled = () => {
      if (isProjectList) {
        return {
          disabled: false,
          disabledTooltip: "",
        };
      }
      if (isProjectGroupItem(itemsForList) && itemsForList.has_existing_build) {
        return {
          disabled: true,
          disabledTooltip: t("tooltip.thisCourseHasBeenPublished"),
        };
      }
      return {
        disabled: false,
        disabledTooltip: "",
      };
    };

    const prettifyFormat = (format: string) => {
      switch (format) {
        case "image":
        case "audio":
        case "avatar":
          return format.replace(/^\w/, (c) => c.toUpperCase());
        case "model3d":
        case "video":
          return t(`pages.gallery.individualMediaType.${format}`);
        case "pdf":
          return "PDF";
        default:
          return format;
      }
    };

    const MediaListMediaName = ({
      name,
      hasBeenGeneratedByAi,
    }: {
      name: string;
      hasBeenGeneratedByAi: boolean;
    }) => {
      if (isMediaList) {
        if (!hasBeenGeneratedByAi) {
          return <p>{name}</p>;
        }
        if (hasBeenGeneratedByAi) {
          return (
            <div className="relative flex flex-row justify-between">
              <p>{name}</p>
              <MadeWithAiIcon width={20} height={20} />
            </div>
          );
        }
      }
      return <p>{name}</p>;
    };

    const displayActionsComponent = () => {
      const deleteStatus = isDeleteDisabled();
      return (
        <DisplayActions
          itemsForList={itemsForList}
          handleClick={handleClick}
          isProjectList={isProjectList}
          deleteOneFromList={isProjectList ? deleteProjectFromList : deleteProjectGroupFromList}
          isDeleteDisabled={deleteStatus.disabled}
          deleteDisabledTooltip={deleteStatus.disabledTooltip}
          setEditedProjectGroupId={setEditedProjectGroupId}
        />
      );
    };

    if (isProjectList) {
      return {
        projectId: itemsForList.id,
        checkbox: displayCheckbox(),
        name: name,
        releaseState: RelevantBadge(isProjectItem(itemsForList) ? itemsForList.status : ""),
        actions: displayActionsComponent(),
      };
    } else if (isMediaList) {
      if (!isMediaItem(itemsForList)) {
        return {
          checkbox: displayCheckbox(),
          name: <MediaListMediaName name={name} hasBeenGeneratedByAi={false} />,
          size: "",
          format: "",
          date: "",
          type: "",
          extension: "",
          actions: displayActionsComponent(),
        };
      }

      const createdAtValue = itemsForList.created_at ?? itemsForList.createdAt;
      const dateMedia = new Date(createdAtValue).toLocaleString(dateConfig(currentLocale));
      const sliceIndexDate =
        currentLocale === "fr" ? dateMedia.indexOf(" ") : dateMedia.indexOf(",");

      return {
        checkbox: displayCheckbox(),
        name: (
          <MediaListMediaName
            name={name}
            hasBeenGeneratedByAi={hasMediaBeenGeneratedByAi(itemsForList)}
          />
        ),
        size: prettifyFileSizeInBytes(
          Number(itemsForList.size_in_bytes),
          currentLocale ? currentLocale : "fr",
        ),
        format: prettifyFormat(itemsForList.type),
        date: dateMedia.substring(0, sliceIndexDate),
        type: itemsForList.media_360_tag
          ? t(`pages.gallery.mediaType.${itemsForList.media_360_tag}`)
          : "",
        extension:
          itemsForList.type === "avatar"
            ? "-"
            : (itemsForList.filename.split(".").pop() ?? "").toUpperCase(),
        actions: (
          <DisplayActions
            itemsForList={itemsForList}
            openMediaDetail={openMediaDetail}
            onDeleteMedia={onDeleteMedia}
            isMediaList
            isModalEdit={isModalEdit}
            setEditedProjectGroupId={setEditedProjectGroupId}
          />
        ),
      };
    } else {
      return {
        projectGroupId: itemsForList.id,
        checkbox: displayCheckbox(),
        name: name,
        typeAuth: translateTypeAuth(
          isProjectGroupItem(itemsForList) ? String(itemsForList.auth_type) : "",
        ),
        releaseState: <PGBadge projectGroupId={itemsForList.id} />,
        actions: displayActionsComponent(),
      };
    }
  });

  const colHeaders = () => {
    if (isProjectList) {
      return [t("general.nameTable"), "Publication", t("general.actions")];
    } else if (isMediaList) {
      return [
        t("general.fileName"),
        t("general.weight"),
        "Format",
        t("general.addDate"),
        "Type",
        "Extension",
        t("general.actions"),
      ];
    } else {
      return [
        t("general.nameTable"),
        t("general.authenticationType"),
        t("general.publicationStatus"),
        t("general.actions"),
      ];
    }
  };

  const titlesModal = () => {
    return isCheck.length > 1
      ? t(`pages.${isProjectList ? "projects" : "projectGroup"}.deletionModal.titlePlural`)
      : t(`pages.${isProjectList ? "projects" : "projectGroup"}.deletionModal.title`);
  };
  const textModal = () => {
    if (isProjectList) {
      return isCheck.length > 1
        ? t("pages.projects.deletionModal.bodyPlural")
        : t("pages.projects.deletionModal.body");
    } else {
      return t("pages.projectGroup.deletionModal.body");
    }
  };

  const onClickSubmitForDeleteProjectOrProjectsGroup = () => {
    if (isProjectList) {
      setIsCheck([]);
      dispatch(closeConfirmationModal("deleteMultipleDeletesConfirmation"));
      void dispatch(
        deleteMultipleProject({
          projectsListId: isCheck.map(Number),
          componentId: `project-${clickedProjectGroupId}`,
        }),
      ).then((res) => {
        if (deleteMultipleProject.fulfilled.match(res)) {
          res.payload.data.map((id: number) => dispatch(removeElementsOfProject(id)));
        }
      });
    } else {
      dispatch(closeConfirmationModal("deleteMultipleDeletesConfirmation"));
      setIsCheck([]);

      dispatch(
        deleteMultipleProjectGroup({
          projectGroupListId: isCheck.map(Number),
          componentId: `project-group-${clickedProjectGroupId}`,
        }),
      )
        .then(() => {
          return dispatch(
            fetchProjectGroupsWithProjects({
              start: page * limit,
              limit,
              sort: projectGroupSearch.sort,
              query: projectGroupSearch.query,
              filterByPublishedProject: isFilteredByPublishedProjects,
              componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
            }),
          );
        })
        .then((result) => {
          if (
            fetchProjectGroupsWithProjects.fulfilled.match(result) &&
            result.payload.data.results.length === 0 &&
            page > 0
          ) {
            dispatch(setSearchPage(page - 1));
            void dispatch(
              fetchProjectGroupsWithProjects({
                start: (page - 1) * limit,
                limit,
                sort: projectGroupSearch.sort,
                query: projectGroupSearch.query,
                filterByPublishedProject: isFilteredByPublishedProjects,
                componentId: PROJECT_GROUP_LIST_WITH_LIMIT_COMPONENT_ID,
              }),
            );
          }
        })
        .catch((error) => {
          console.error("Bulk deletion or refetch failed:", error);
        });
    }
  };
  const displayCheckAll = () => {
    if (isMediaList) {
      if (isModalEdit) {
        if (filterPrivateProjectGroup.length > 0) {
          return checkAllProjectGroupComponent();
        } else {
          return <></>;
        }
      } else {
        return <></>;
      }
    } else {
      return checkAllProjectGroupComponent();
    }
  };

  return (
    <>
      <IsLoading componentId={`project-${clickedProjectGroupId}`} showSuccess={false}>
        {arrayItemsForList.length > 0 ? (
          <div className="h-full flex w-full flex-col">
            {isCheck.length > 0 && (
              <div className="h-[2rem]">
                <h3>
                  {isCheck.length} {deleteSelection}
                  {" - "}
                  <button onClick={() => openModalDeleteMultiple()}>
                    <div className="flex">
                      <p className="text-red-500">{deleteSelectionButtonText()}</p>

                      <TrashIcon className="w-5 h-5 stroke-red-500 mr-3" />
                    </div>
                  </button>
                </h3>
              </div>
            )}

            <div className={"overflow-y-auto personalize-scroll-visible w-full"}>
              <TableComp
                handleClick={handleClick}
                isProjectList={isProjectList}
                isMediaList={isMediaList}
                rows={getColumnValues}
                colHeaders={colHeaders()}
                nonSortableColumns={[t("general.actions")]}
                checkAll={displayCheckAll()}
                listTofilter={stuffToDisplay}
                setListOfItemsFilter={setStuffToDisplay}
                isProjectGroup={isProjectGroup}
                className="w-full"
              />
            </div>
            {!hidePaginationControls && (
              <PaginationControls
                currentPage={page}
                totalPages={totalPages}
                onPageChange={(params) =>
                  handlePageChange({
                    ...params,
                    extraParams: { filterByPublishedProject: isFilteredByPublishedProjects },
                  })
                }
                isLoading={projectGroupsAreLoading}
              />
            )}
          </div>
        ) : (
          <div>
            <NoItemsList isModeProjectList={isProjectList} isModeMediaList={isMediaList} />
          </div>
        )}
      </IsLoading>
      <ModalConfirmation
        id="deleteMultipleDeletesConfirmation"
        onClickSubmit={() => {
          onClickSubmitForDeleteProjectOrProjectsGroup();
        }}
        title={titlesModal()}
        text={textModal()}
        componentId={PROJECT_GROUP_LIST_COMPONENT_ID}
      />
    </>
  );
};

export default ModeListProjectGroup;
